import { useSyncExternalStore } from 'react';

/**
 * Installing the counter as an app, and why it is a storage feature rather than
 * a cosmetic one.
 *
 * **Chromium never asks the user about persistent storage.** `persist()` does
 * not prompt — it decides by heuristic and resolves, and on current versions the
 * signal that flips it is whether the site is INSTALLED. So a counter that is
 * only ever a tab gets told "no", quietly, forever, and its queue of unsent
 * bills stays evictable no matter how many times anyone presses a button.
 * Installing is the lever. (Firefox is the other way round: it prompts, and
 * installing is irrelevant.)
 *
 * The window it gets is worth having too — no address bar, launches from the
 * taskbar, survives a browser restart — but that is the bonus, not the reason.
 *
 * `beforeinstallprompt` fires once, early, and only if the browser considers
 * the app installable. It has to be caught at startup rather than when the
 * settings screen opens, which is why this module is imported from `main.tsx`
 * for its side effect.
 */

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

/** True when running as an installed app rather than in a browser tab. */
function isInstalled(): boolean {
  try {
    return (
      globalThis.matchMedia?.('(display-mode: standalone)').matches === true ||
      // iOS Safari, which predates the standard and never adopted it.
      (navigator as { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

/*
 * `preventDefault` stops Chromium's own install chip so the offer appears where
 * it makes sense — next to the storage row it actually fixes — rather than as
 * an unexplained icon in the address bar.
 */
window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  deferred = event as InstallPromptEvent;
  emit();
});

window.addEventListener('appinstalled', () => {
  deferred = null;
  emit();
});

type InstallState = 'installed' | 'available' | 'unavailable';

let snapshot: InstallState = 'unavailable';

function state(): InstallState {
  const next: InstallState = isInstalled()
    ? 'installed'
    : deferred
      ? 'available'
      : 'unavailable';
  // useSyncExternalStore compares by identity, so the same string has to come
  // back every time or React re-renders forever.
  if (next !== snapshot) snapshot = next;
  return snapshot;
}

export function useInstallState(): InstallState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    state,
    () => 'unavailable' as const,
  );
}

/**
 * Shows the browser's install dialog. Resolves to what the person chose.
 *
 * The captured event is single-use: whether it is accepted or dismissed, it
 * cannot be shown again until the browser decides to offer another.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable';
  const event = deferred;
  deferred = null;
  emit();
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  } catch {
    return 'dismissed';
  }
}
